/**
 * The Pages window: every node of the environment as a page, in the tree of its groups, like a
 * wiki's sidebar (LegendKeeper's article tree). Groups are folders (open or closed, kept while
 * the window moves), nodes inside sorted by name, then those in no group. The search narrows
 * by name or by words of the page. Clicking a page opens it in the Page window.
 */
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import type { Group as GraphGroup } from "@protocol/Group";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { Dot } from "../../shared/ui";

/** Folders closed and the search, kept outside the component so they survive a window move. */
const [closed, setClosed] = createSignal<ReadonlySet<string>>(new Set());
const [search, setSearch] = createSignal("");

/** Whether `node` matches the search `q` (lower case): its name or its page. */
export function pageMatches(node: Pick<Node, "name" | "page">, q: string): boolean {
  return !q || node.name.toLowerCase().includes(q) || (node.page ?? "").toLowerCase().includes(q);
}

/** The window. */
export function PagesWindow(): JSX.Element {
  const { graph, pages, nodeAdder, view } = useApp();
  const q = () => search().trim().toLowerCase();
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  const nodes = createMemo(() => graph.state.graph.nodes.filter((n) => pageMatches(n, q())).sort(byName));
  const groups = () => graph.state.graph.groups;
  /** Whether group `g` holds a node shown, itself or below. */
  const holds = (g: GraphGroup): boolean => nodes().some((n) => n.group === g.id) || groups().some((c) => c.parent === g.id && holds(c));
  const children = (parent: string | null) => groups().filter((g) => (g.parent ?? null) === parent && holds(g)).sort(byName);
  const toggle = (id: string) =>
    setClosed((s) => {
      const next = new Set(s);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  /** One page of the tree. */
  const Item = (props: { node: Node }) => (
    <li>
      <button
        type="button"
        class="page-item"
        classList={{ current: pages.current()?.id === props.node.id, written: !!props.node.page }}
        data-page-item={props.node.id}
        onClick={() => pages.show(props.node.id)}
      >
        <Dot node={props.node} />
        <span>{props.node.name}</span>
      </button>
    </li>
  );
  /** A group and what it holds. */
  const Folder = (props: { group: GraphGroup }): JSX.Element => (
    <li class="page-folder">
      <button type="button" class="page-folder-name" data-page-folder={props.group.id} aria-expanded={!closed().has(props.group.id) || !!q()} onClick={() => toggle(props.group.id)}>
        <span class="chev">{!closed().has(props.group.id) || q() ? "▾" : "▸"}</span> {props.group.name}
      </button>
      <Show when={!closed().has(props.group.id) || q()}>
        <ul>
          <For each={children(props.group.id)}>{(g) => <Folder group={g} />}</For>
          <For each={nodes().filter((n) => n.group === props.group.id)}>{(n) => <Item node={n} />}</For>
        </ul>
      </Show>
    </li>
  );

  return (
    <div class="pages-window" id="pages">
      <div class="pages-bar">
        <input id="pages-search" class="input" type="search" placeholder="Search pages" value={search()} onInput={(e) => setSearch(e.currentTarget.value)} />
        <button type="button" class="btn" id="pages-new" title="A new page: a node of this environment" onClick={() => nodeAdder.open()}>
          New
        </button>
      </div>
      <Show when={nodes().length} fallback={<p class="muted small">{q() ? "No page matches." : "No pages yet: make one with New."}</p>}>
        <ul class="pages-tree">
          <For each={children(null)}>{(g) => <Folder group={g} />}</For>
          <For each={nodes().filter((n) => !n.group || !groups().some((g) => g.id === n.group))}>{(n) => <Item node={n} />}</For>
        </ul>
      </Show>
      <Show when={view.state.selection === null && !pages.current()}>
        <p class="muted small">Pick a page to read it.</p>
      </Show>
    </div>
  );
}
